import { MaskLine, Rise } from "@/components/motion/Reveal";
import { TEAM, ASSOCIATES } from "@/data/site";
import { PROFILES } from "@/data/profiles";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/seo/Seo";
import { ldBreadcrumb, ldPerson } from "@/seo/seoConfig";

export default function TeamPage() {
  const { t } = useLang();
  return (
    <div className="pt-40">
      <Seo
        title="The Bench · Founders, Counsel & Associates"
        description="Meet the founding partners of RPLC UAE — Raunak Rane (Corporate & Commercial, Technology, AI Governance, Cross-Border) and Rajas Pingle (Cyber Law, Cyber Security, Digital Frauds, Data Protection & Privacy) — and the firm's associates."
        path="/team"
        keywords={["Raunak Rane lawyer", "Rajas Pingle cyber lawyer", "Dubai lawyer", "cyber law lawyer Pune", "technology lawyer Dubai", "data protection lawyer India"]}
        jsonLd={[ldBreadcrumb([{ name: "Home", path: "/" }, { name: "Team", path: "/team" }]), ...PROFILES.map((p) => ldPerson(p))]}
      />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise><div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow">{t.teamPage.eyebrow}</span></div></Rise>
        <h1 className="font-serif tracking-tight leading-[0.9] text-[13vw] lg:text-[8vw]">
          <MaskLine delay={0.15}>{t.teamPage.title1}</MaskLine>
          <MaskLine delay={0.32}><span className="italic-accent">{t.teamPage.title2}</span></MaskLine>
        </h1>
      </section>

      {/* FOUNDING PARTNERS */}
      <section className="mt-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 space-y-32 lg:space-y-48">
          {TEAM.map((m, i) => {
            const isEven = i % 2 === 0;
            const profile = PROFILES[i];
            return (
              <div key={m.name} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start`}>
                <Rise className={`lg:col-span-6 ${isEven ? "" : "lg:order-2"}`} y={30}>
                  <Link to={`/team/${profile?.slug || ""}`} className="block relative overflow-hidden border border-[#141414] group" data-testid={`team-partner-${profile?.slug || i}`}>
                    <img src={m.img} alt={m.name} className="w-full h-[560px] lg:h-[720px] object-cover object-top photo-mono group-hover:scale-105 transition-transform duration-1000 ease-out" />
                    <div className="absolute top-4 left-4 chapter-num text-[#F4F1EB]">{("0" + (i + 1)).slice(-2)} · {TEAM.length}</div>
                    <div className="absolute bottom-4 right-4 chapter-num text-[#F4F1EB] flex items-center gap-2 border border-[#F4F1EB]/40 px-3 py-1.5 backdrop-blur-sm bg-[#050505]/40">
                      Full profile <ArrowUpRight size={12} strokeWidth={1.25} />
                    </div>
                  </Link>
                </Rise>
                <Rise delay={0.1} className={`lg:col-span-6 ${isEven ? "" : "lg:order-1"}`}>
                  <div className="eyebrow">{m.role}</div>
                  <Link to={`/team/${profile?.slug || ""}`} className="link-hover inline-block">
                    <h2 className="font-serif text-5xl lg:text-7xl mt-6 leading-[0.95] tracking-tight">{m.name}</h2>
                  </Link>
                  <div className="italic-accent text-[#A19D94] text-2xl mt-3">{m.focus}</div>
                  <p className="mt-8 text-[#A19D94] text-lg leading-relaxed max-w-xl">{m.bio}</p>
                  <div className="mt-10 flex flex-wrap gap-3">
                    {["UAE", "India", "USA", "Cross-border"].map((tag) => (
                      <span key={tag} className="chapter-num border border-[#222] px-2.5 py-1">{tag}</span>
                    ))}
                  </div>
                  <Link to={`/team/${profile?.slug || ""}`} className="btn-ghost mt-10 inline-flex">
                    Read full profile <ArrowUpRight size={14} strokeWidth={1.5} />
                  </Link>
                </Rise>
              </div>
            );
          })}
        </div>
      </section>

      {/* ASSOCIATES */}
      <section data-testid="team-associates" className="mt-40 border-t border-[#141414] pt-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">Associates · The Bench</span></div></Rise>
              <Rise delay={0.1}>
                <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight">
                  One firm, <span className="italic-accent">one team.</span>
                </h2>
              </Rise>
              <Rise delay={0.2}>
                <p className="mt-8 text-[#A19D94] text-lg max-w-2xl leading-relaxed">
                  A bench built on trust, discipline and modern legal craft — combining courtroom experience with technology, data protection and corporate expertise.
                </p>
              </Rise>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {ASSOCIATES.map((a, i) => (
              <Rise key={a.name} delay={i * 0.05}>
                <div className="bg-[#050505] group h-full flex flex-col border border-[#141414]">
                  <div className="relative overflow-hidden aspect-[4/5] border-b border-[#141414]">
                    <img
                      src={a.img}
                      alt={a.name}
                      loading="lazy"
                      className="w-full h-full object-cover object-top photo-mono group-hover:scale-[1.04] transition-transform duration-1000 ease-out"
                    />
                    <div className="absolute top-4 left-4 chapter-num text-[#F4F1EB]">{("0" + (i + 1)).slice(-2)} / {String(ASSOCIATES.length).padStart(2, "0")}</div>
                  </div>
                  <div className="p-6 lg:p-8 flex-1 flex flex-col">
                    <div className="chapter-num text-[#C84C35]">{a.role}</div>
                    <h3 className="font-serif text-3xl mt-3 leading-tight">{a.name}</h3>
                    <p className="mt-4 text-[#A19D94] text-sm leading-relaxed flex-1">{a.bio}</p>
                  </div>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 lg:py-48 mt-20 border-t border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Rise>
              <h2 className="font-serif text-5xl lg:text-[7vw] leading-[0.95] tracking-tight">
                {t.teamPage.close.split(" ").map((w, i) => (
                  <span key={i} className={i === 1 ? "italic-accent" : ""}>{w} </span>
                ))}
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-4 lg:justify-end flex">
            <Rise delay={0.1}><Link to="/contact" className="btn-primary">{t.contactCTA.cta} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
          </div>
        </div>
      </section>
    </div>
  );
}
